import { useState } from "react";

/**
 * Custom hook for managing expandable rows in tables
 * @returns An object with expandedRows Set and a toggle function
 */
export const useExpandableRows = () => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const isExpanded = (id: string) => expandedRows.has(id);

  return { expandedRows, toggleRow, isExpanded };
};

/**
 * Custom hook for managing delete confirmations
 * @param entityName - The name of the entity type being deleted (e.g., "Lehrperson", "Klasse")
 * @param onDelete - Callback function to execute when deletion is confirmed
 * @returns A function that handles the delete confirmation dialog
 */
export const useDeleteConfirmation = (
  entityName: string,
  onDelete: (id: string) => void
) => {
  const confirmDelete = (id: string, name?: string) => {
    const displayName = name ? ` "${name}"` : "";
    if (window.confirm(`${entityName}${displayName} wirklich löschen?`)) {
      onDelete(id);
    }
  };

  return confirmDelete;
};

/**
 * Custom hook for managing form state with reset functionality
 * @param initialState - The initial state object for the form
 * @returns An object with formData, setFormData, and resetForm functions
 */
export const useFormReset = <T extends Record<string, any>>(
  initialState: T
) => {
  const [formData, setFormData] = useState<T>(initialState);

  const resetForm = () => {
    setFormData(initialState);
  };

  return { formData, setFormData, resetForm };
};
